import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Company } from '../types';
import apiClient from '../core/apiClient';
import { SubscriptionStatusResponse } from '../types/api';

interface TenantContextType {
  company: Company | null;
  setCompany: (company: Company | null) => void;
  subscription: SubscriptionStatusResponse | null;
  loading: boolean;
  isSubscriptionActive: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!company?.companyId) {
        setSubscription(null);
        return;
      }
      
      setLoading(true);
      try {
        const response = await apiClient.get<SubscriptionStatusResponse>(`/api/subscription/status/${company.companyId}`);
        setSubscription(response.data);
      } catch (err) {
        console.error('Failed to fetch subscription status', err);
        // Fallback for hardcoded testing since we don't have a backend
        if (company.companyId === 'comp-1') {
          setSubscription({ status: 'ACTIVE' } as any);
        } else {
          setSubscription({ status: 'EXPIRED' } as any);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [company?.companyId]);

  const isSubscriptionActive = 
    subscription?.status === 'ACTIVE' || 
    subscription?.status === 'GRACE_PERIOD';

  return (
    <TenantContext.Provider value={{ 
      company, 
      setCompany, 
      subscription, 
      loading, 
      isSubscriptionActive 
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
